/**
 * **How far a client is along a goal**, for the `goalRenewalDue` card: the bar from start to target
 * with "teraz" on it, and how much is left.
 *
 * The fraction is the SERVER's (`pct`), clamped, never recomputed: a reduction goal runs backwards
 * (78 → 72 kg) and a `Custom` goal has no metric at all while still carrying a percentage the
 * trainer set, and a second formula here would disagree with the client detail's goal card on both.
 * The three figures are formatted by `entities/client`'s `goalFigureText`, the one the goal card and
 * the roster preview already share.
 *
 * ── Nothing measured means no bar, not an empty one ──────────────────────────────────────────
 * A measurable goal whose current value was never recorded has no "teraz"; a bar drawn at zero
 * would tell the trainer the client has made no progress, which nobody knows.
 *
 * Beside the bar, on the desk: the goal's timeline and the series it is judged on (`goalTimeline`,
 * `goalReading`, `goalWeightPoints`).
 *
 * Pure: no React, no DOM, no query hook. `t` and the locale arrive through `GoalFigureDeps`.
 */

import { goalFigureText, type GoalFigureDeps } from '~/entities/client';
import { parseDateOnly } from '~/shared/lib/calendarDate';
import type { TrainWithMeModulesClientsDtosResponsesCustomerGoalResponse as CustomerGoal } from '~/shared/api/generated/model';

export interface GoalProgress {
  readonly goalId: number;
  readonly title: string;
  /** The goal's deadline, the civil day. */
  readonly deadline: string | null;
  /** 0–1, the server's own percentage. `null` where nothing was measured. */
  readonly fraction: number | null;
  readonly startText: string | null;
  readonly nowText: string | null;
  readonly targetText: string | null;
  /** "7,5 kg" — the distance still to go, or `null` where it cannot be said. */
  readonly remainingText: string | null;
}

export function goalProgress(goal: CustomerGoal, deps: GoalFigureDeps): GoalProgress | null {
  if (typeof goal.id !== 'number') return null;
  const unit = goal.targetUnit ?? null;
  const now = goal.currentValue ?? null;
  const target = goal.targetValue ?? null;
  const measured = now !== null && typeof goal.pct === 'number' && Number.isFinite(goal.pct);

  return {
    goalId: goal.id,
    title: goal.title?.trim() || goal.exerciseName?.trim() || '',
    deadline: goal.deadline?.slice(0, 10) ?? null,
    fraction: measured ? Math.min(1, Math.max(0, (goal.pct ?? 0) / 100)) : null,
    startText: goalFigureText(goal.startValueSnapshot, unit, deps),
    nowText: measured ? goalFigureText(now, unit, deps) : null,
    targetText: goalFigureText(target, unit, deps),
    remainingText:
      measured && target !== null && Math.abs(target - now) > 0
        ? goalFigureText(Math.abs(target - now), unit, deps)
        : null,
  };
}

/**
 * The goal's span, from the day it was set to its deadline, and how much of it has passed — the
 * desk card's timeline (owner, 26 September 2026). `null` where either end is missing or the span
 * is not a span (a deadline on or before the day it was set).
 */
export interface GoalTimeline {
  /** The civil day the goal was set. */
  readonly from: string;
  /** The deadline's civil day. */
  readonly to: string;
  /** 0–1: today's place between the two, clamped at both ends. */
  readonly elapsed: number;
}

export function goalTimeline(goal: CustomerGoal, todayIso: string): GoalTimeline | null {
  const from = goal.createdAt?.slice(0, 10) ?? null;
  const to = goal.deadline?.slice(0, 10) ?? null;
  if (from === null || to === null) return null;
  const start = parseDateOnly(from);
  const end = parseDateOnly(to);
  const today = parseDateOnly(todayIso);
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(today) || end <= start) return null;
  return { from, to, elapsed: Math.min(1, Math.max(0, (today - start) / (end - start))) };
}

/**
 * What the card reads beside the bar, by the goal's type (backend `GoalType`): a weight goal the
 * client's body weight, a lift goal its movement's trend. A session count and a free-text goal have
 * no series of their own — the bar and the timeline are the whole story.
 */
export type GoalReading =
  | { readonly kind: 'weight' }
  | { readonly kind: 'lift'; readonly exerciseId: number }
  | { readonly kind: 'none' };

export function goalReading(goal: CustomerGoal): GoalReading {
  if (goal.type === 'WeightTarget') return { kind: 'weight' };
  if (goal.type === 'PrBased' && typeof goal.exerciseId === 'number') return { kind: 'lift', exerciseId: goal.exerciseId };
  return { kind: 'none' };
}

/** One body-weight snapshot, in the only terms the goal's chart needs. */
export interface WeightSnapshot {
  readonly recordedAt?: string | null;
  readonly weightKg?: number | null;
}

/**
 * The weight goal's series: every weighed snapshot since the goal was set, oldest first — the
 * readings the goal is judged on. Where fewer than two fall inside the goal's span, the whole
 * history, so a goal set last week still draws the line it was set against.
 */
export function goalWeightPoints(
  snapshots: readonly WeightSnapshot[],
  since: string | null,
): readonly { readonly x: Date; readonly y: number }[] {
  const all = snapshots
    .flatMap((snapshot) => {
      const at = snapshot.recordedAt == null ? Number.NaN : Date.parse(snapshot.recordedAt);
      const kg = snapshot.weightKg;
      return Number.isFinite(at) && typeof kg === 'number' && Number.isFinite(kg) ? [{ at, kg }] : [];
    })
    .sort((left, right) => left.at - right.at);
  const start = since === null ? Number.NaN : parseDateOnly(since);
  const inside = Number.isFinite(start) ? all.filter((reading) => reading.at >= start) : all;
  return (inside.length >= 2 ? inside : all).map((reading) => ({ x: new Date(reading.at), y: reading.kg }));
}
